import {
	HttpClient,
	HttpContext,
	HttpErrorResponse,
	HttpHeaders,
	HttpParams,
	HttpResponse
}                               from '@angular/common/http';
import { inject }               from '@angular/core';
import { LoggerService }        from '@app/shared/services/logger.service';
import { API_CONFIG }           from '@appConfig/api-config';
import {
	IHttpOptions,
	RequestActionEnum,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                               from '@core/interfaces/api.interface';
import { AuthorizationService } from '@core/services/authorization/authorization.service';
import {
	HEADER_CLIENT_ANALYTICS_INTERCEPTOR,
	HEADER_CLIENT_INTERCEPTOR,
	HEADER_MANAGED_TENANT_INTERCEPTOR,
	IHeaderClientAnalyticsInterceptor,
	IHeaderClientInterceptor,
	IHeaderManagedTenantInterceptor,
	IHttpContextConfig,
	InterceptorXHeaderIdentifierEnum
}                               from '@interceptors/interceptors.model';
import {
	ApplicationScopeTypes,
	ScopePermissionCheckEnum
}                               from '@models/scope.enums';
import { IPaging }              from '@shared/list-operations/model/paging.model';
import { ArrayUtils }           from '@utils/helpers';
import {
	Observable,
	of,
	Subject
}                               from 'rxjs';
import {
	catchError,
	finalize,
	take
}                               from 'rxjs/operators';


export abstract class AbstractApiService {

	private readonly _authorizationService: AuthorizationService = inject( AuthorizationService );

	protected hasAllPermissions( scopes: ApplicationScopeTypes[], permissionCheck: ScopePermissionCheckEnum = ScopePermissionCheckEnum.AND ): boolean {
		return this._authorizationService.hasAllPermissions( scopes, permissionCheck );
	}


	protected readonly arrayUtils = ArrayUtils;
	protected abstract readonly logger: LoggerService;

	protected abstract readonly http: HttpClient;

	protected basePath = API_CONFIG.API_BASE_PATH;

	private generateHttpOptions( method: string, entity: any, headers?: {}, params?: {}, observe?: boolean ): IHttpOptions {
		const httpOptions: IHttpOptions = { headers: null };

		headers = headers ? { 'Content-Type': 'application/json', ...headers } : { 'Content-Type': 'application/json' };

		switch ( method ) {
			case 'list':
				break;
			case 'read':
				headers = { ...headers, 'access-control-expose-headers': 'ETag' };
				break;
			case 'create':
				break;
			case 'update':
				if ( entity.etag ) {
					headers = { ...headers, 'If-Match': entity.etag };
					delete entity.etag;
				}
				break;
			case 'delete':
				break;
		}


		httpOptions.headers = new HttpHeaders( { 'Content-Type': 'application/json', ...headers } );
		if ( params ) { httpOptions.params = new HttpParams( { fromObject: params } ); }
		if ( observe ) { httpOptions.observe = 'response' as 'body'; }

		// this.logger.debug( `httpOptions: `, httpOptions );
		return httpOptions;
	}

	protected baseList<T>( url: string, params?: {} ): Observable<T> {
		const httpOptions = this.generateHttpOptions(
			'list',
			null,
			null,
			params
		);

		return this.http.get<T>( url, httpOptions );
	}

	protected baseRead<T>( url: string, params?: {}, withFullResponse?: boolean ): Observable<T> {
		const httpOptions = this.generateHttpOptions(
			'read',
			null,
			null,
			params,
			withFullResponse
		);

		return this.http.get<T>( url, httpOptions );
	}

	protected baseCreate<T, U>( url: string, reqBody: U, deleteFields: string[] = [], params? ): Observable<T> {
		const httpOptions = this.generateHttpOptions(
			'create',
			reqBody,
			null,
			params
		);

		return this.http.post<T>( url, reqBody, httpOptions );
	}

	protected baseUpdate<T, U>( url: string, reqBody: U, deleteFields: string[] = [], params?: {} ): Observable<T> {
		const httpOptions = this.generateHttpOptions(
			'update',
			reqBody,
			null,
			params
		);

		return this.http.put<T>( url, reqBody, httpOptions );
	}

	protected basePatchUpdate<T, U>( url: string, reqBody: U, deleteFields: string[] = [], params? ): Observable<T> {
		const httpOptions = this.generateHttpOptions(
			'read',
			reqBody,
			null,
			params
		);

		return this.http.patch<T>( url, reqBody, httpOptions );
	}

	protected baseDelete<T, U>( url: string, reqBody: U, params? ): Observable<T> {
		const httpOptions = this.generateHttpOptions(
			'delete',
			null,
			null,
			params
		);

		return this.http.delete<T>( url, httpOptions );
	}

	protected getRequestCompleteEntityState<T>(
		options: {
			action: RequestActionEnum;
			debug?: { message: string, response: any };
			error?: HttpErrorResponse;
			httpResponse?: HttpResponse<T>;
			requestState?: RequestState;
			entity?: T;
		}
	): RequestCompleteEntityState<T> {
		const {
			action,
			debug,
			error,
			httpResponse,
			requestState = RequestState.NOT_ASKED,
			entity
		} = options;

		return {
			action,
			debug,
			error,
			httpResponse,
			requestState,
			entity
		};
	}

	protected getRequestCompleteListState<T>(
		options: {
			action: RequestActionEnum;
			debug?: { message: string, response: any };
			error?: HttpErrorResponse;
			httpResponse?: HttpResponse<T>;
			requestState?: RequestState;
			list?: T[];
			paging?: IPaging;
			totalCount?: number;
		}
	): RequestCompleteListState<T> {
		const {
			action,
			debug,
			error,
			httpResponse,
			requestState = RequestState.NOT_ASKED,
			list,
			paging = { pageNumber: 0, pageSize: 20, pageCount: 1 },
			totalCount
		} = options;

		return {
			action,
			debug,
			error,
			httpResponse,
			requestState,
			list,
			paging,
			totalCount
		};
	}

	protected buildHttpContext( httpContextConfig: IHttpContextConfig ): { context: HttpContext } {
		const httpContext: HttpContext = new HttpContext();

		if ( httpContextConfig?.headerClient ) {
			httpContext.set<IHeaderClientInterceptor>(
				HEADER_CLIENT_INTERCEPTOR,
				{ ...httpContextConfig.headerClient, xHeader: InterceptorXHeaderIdentifierEnum.HEADER_CLIENT }
				?? { enabled: true, clientIdentifier: 'FCP', xHeader: InterceptorXHeaderIdentifierEnum.HEADER_CLIENT } // this header should be always set
			);
		}

		if ( httpContextConfig?.headerClientAnalytics ) {
			httpContext.set<IHeaderClientAnalyticsInterceptor>(
				HEADER_CLIENT_ANALYTICS_INTERCEPTOR,
				{ ...httpContextConfig.headerClient, xHeader: InterceptorXHeaderIdentifierEnum.HEADER_CLIENT_ANALYTICS }
				?? { enabled: true, xHeader: InterceptorXHeaderIdentifierEnum.HEADER_CLIENT_ANALYTICS } // this header should be always set
			);
		}

		if ( httpContextConfig?.headerManagedTenant ) {
			httpContext.set<IHeaderManagedTenantInterceptor>(
				HEADER_MANAGED_TENANT_INTERCEPTOR,
				{ ...httpContextConfig.headerManagedTenant, xHeader: InterceptorXHeaderIdentifierEnum.HEADER_MANAGED_TENANT }
			);
		}

		// if ( httpContextConfig?.headerManagedTenantRealm ) {
		// 	httpContext.set<IHeaderManagedTenantRealmInterceptor>(
		// 		HEADER_MANAGED_TENANT_REALM_INTERCEPTOR,
		// 		{ ...httpContextConfig.headerManagedTenantRealm, xHeader: InterceptorXHeaderIdentifierEnum.HEADER_MANAGED_TENANT_REALM }
		// 	);
		// }

		return { context: httpContext };
	}

	protected blobToRequestCompleteEntityState( req$: Observable<HttpResponse<Blob>> ): Observable<RequestCompleteEntityState<Blob>> {
		const subject: Subject<RequestCompleteEntityState<Blob>> = new Subject<RequestCompleteEntityState<Blob>>();

		req$
		.pipe(
			take( 1 ),
			catchError( ( error: HttpErrorResponse ) => {
				subject.next( {
					requestState: RequestState.FAILED,
					error,
					entity: null,
					action: RequestActionEnum.DOWNLOAD
				} );

				return of( null );
			} ),
			finalize( () => {
				subject.next( null );
				subject.complete();
				subject.unsubscribe();
			} )
		)
		.subscribe( ( httpResponse: HttpResponse<Blob> ) => {
			subject.next( {
				requestState: RequestState.SUCCESS,
				httpResponse,
				entity: httpResponse?.body,
				action: RequestActionEnum.DOWNLOAD
			} );
		} );

		return subject.asObservable();
	}
}
